import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Zap, Send, Clock, CheckCircle2, AlertCircle, ChevronRight } from "lucide-react";

/**
 * Mini-resumo de Automações no Dashboard. Substitui as 3 telas duplicadas
 * (WeeklySummaryCard + SmartLeadsCard + Atalhos de Campanha).
 * Pede `/api/automations/digest` e mostra:
 *   - Enviadas hoje
 *   - Aguardando autorização (manuais não disparadas)
 *   - Próximo envio agendado
 * Cada bloco navega pro /automacoes com a aba apropriada.
 */
export default function AutomationsDigestCard({ apiCall, navigate: navProp }) {
  const nav = navProp || useNavigate();  // eslint-disable-line react-hooks/rules-of-hooks
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCall("get", "/automations/digest")
      .then((r) => setDigest(r.data))
      .catch(() => setDigest({ sent_today: 0, pending_manual: 0, next_run_at: null, active_count: 0, eligible_total: 0 }))
      .finally(() => setLoading(false));
  }, [apiCall]);

  const blocks = [
    {
      label: "Enviadas hoje",
      value: digest?.sent_today ?? 0,
      icon: Send,
      color: "#16a34a",
      bg: "#dcfce7",
      to: "/automacoes?tab=historico&filter=today",
      testid: "digest-sent-today",
    },
    {
      label: "Aguardando autorização",
      value: digest?.pending_manual ?? 0,
      icon: AlertCircle,
      color: "#d97706",
      bg: "#fef3c7",
      to: "/automacoes?tab=ativas&type=manual",
      testid: "digest-pending-manual",
    },
    {
      label: "Clientes elegíveis",
      value: digest?.eligible_total ?? 0,
      icon: CheckCircle2,
      color: "#7c3aed",
      bg: "#f5f3ff",
      to: "/automacoes?tab=ativas",
      testid: "digest-eligible-total",
    },
    {
      label: "Próximo envio",
      value: digest?.next_run_at ? new Date(digest.next_run_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—",
      icon: Clock,
      color: "#0891b2",
      bg: "#ecfeff",
      to: "/automacoes?tab=ativas&filter=scheduled",
      testid: "digest-next-run",
    },
  ];

  return (
    <Card
      className="border border-border bg-card p-3 shadow-soft rounded-md cursor-pointer hover:shadow-md transition-shadow"
      data-testid="automations-digest-card"
      onClick={() => nav("/automacoes")}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Filtros e Leads · Resumo de hoje</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {digest?.active_count != null && <span>{digest.active_count} automação(ões) ativa(s)</span>}
          <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-2 text-center">Carregando…</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {blocks.map((b) => {
            const Icon = b.icon;
            return (
              <button
                key={b.label}
                type="button"
                onClick={(e) => { e.stopPropagation(); nav(b.to); }}
                className="flex items-center gap-2 p-2 rounded border border-border bg-background hover:bg-secondary/30 transition-colors text-left"
                data-testid={b.testid}
              >
                <div className="flex items-center justify-center h-7 w-7 rounded-md flex-shrink-0" style={{ background: b.bg }}>
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.5} style={{ color: b.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold" style={{ color: b.color }}>{b.value}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{b.label}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
